import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TabPanel, { a11yProps } from '../misc/TabsPanel';
import LegalContent, { LegalContentType } from './LegalContent';
import Typography from '@mui/material/Typography';
import { parseEnumFromString, parseStringFromEnum, TAB_QUERY_STRING } from 'utils/url-tabs-helper';
import ChangeHistory from './ChangeHistory';
import { FilterKeys } from '@common/enums/query-params/filters';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useAppSelector } from 'store';
import { selectUser } from 'store/general/general-slice-selectors';
import { usePublicLayout } from '../public/PublicLayoutContext';

export enum PageTabs {
  TermsOfService,
  PrivacyPolicy,
  ChangeHistory,
}

const LegalPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const matchesSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [filterParams, setFilterParams] = useSearchParams();
  const date = filterParams.get(FilterKeys.StartDate);
  const pageTab = date
    ? PageTabs.ChangeHistory
    : parseEnumFromString(PageTabs, filterParams.get(TAB_QUERY_STRING));

  const user = useAppSelector((state) => selectUser(state));

  const { isInPublicLayout, language: publicLayoutLanguage } = usePublicLayout();
  const [tabsValue, setTabsValue] = useState(pageTab);

  const language = isInPublicLayout ? publicLayoutLanguage : user.data.language;

  const tabChangeHandler = useCallback(
    (tab: PageTabs) => {
      setFilterParams((prevState) => {
        prevState.set(TAB_QUERY_STRING, parseStringFromEnum(PageTabs, tab));
        prevState.delete(FilterKeys.StartDate);
        return prevState;
      });
      setTabsValue(tab);
    },
    [setFilterParams],
  );

  useEffect(() => {
    if (tabsValue !== pageTab) {
      tabChangeHandler(pageTab);
    }
  }, [pageTab, tabChangeHandler, tabsValue]);

  const tabChangeClickHandler = (_event: React.SyntheticEvent, newValue: number) => {
    tabChangeHandler(newValue);
  };

  return (
    <React.Fragment>
      <Stack spacing={2} p={2} sx={{ maxHeight: '100%', overflow: 'hidden' }}>
        <Typography variant="h2" fontSize={24} lineHeight={2}>
          {`Sphere - ${t('button-legal')}`}
        </Typography>

        <Tabs
          value={tabsValue}
          onChange={tabChangeClickHandler}
          variant={matchesSmDown ? 'fullWidth' : 'scrollable'}
          scrollButtons={false}
        >
          <Tab
            label={t('title-terms-of-service')}
            {...a11yProps(PageTabs.TermsOfService)}
            sx={matchesSmDown ? { minWidth: 0, px: 1 } : undefined}
          />
          <Tab
            label={t('title-privacy-policy')}
            {...a11yProps(PageTabs.PrivacyPolicy)}
            sx={matchesSmDown ? { minWidth: 0, px: 1 } : undefined}
          />
          <Tab
            label={t('title-change-history')}
            {...a11yProps(PageTabs.ChangeHistory)}
            sx={matchesSmDown ? { minWidth: 0, px: 1 } : undefined}
          />
        </Tabs>

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <TabPanel value={tabsValue} index={PageTabs.TermsOfService}>
            <LegalContent
              type={LegalContentType.TermsOfService}
              date={date ?? undefined}
              language={language}
            />
          </TabPanel>
          <TabPanel value={tabsValue} index={PageTabs.PrivacyPolicy}>
            <LegalContent
              type={LegalContentType.PrivacyPolicy}
              date={date ?? undefined}
              language={language}
            />
          </TabPanel>
          <TabPanel value={tabsValue} index={PageTabs.ChangeHistory}>
            <ChangeHistory
              date={date ?? undefined}
              language={language}
              type={
                parseEnumFromString(PageTabs, filterParams.get(TAB_QUERY_STRING)) ===
                PageTabs.PrivacyPolicy
                  ? LegalContentType.PrivacyPolicy
                  : LegalContentType.TermsOfService
              }
            />
          </TabPanel>
        </Box>
      </Stack>
    </React.Fragment>
  );
};

export default LegalPage;
